import { Refusal, type Client } from '@snapshots/transport';
import type { Descriptor } from './types';

/** Where the person is: the project and the organization the family bar should name */
export interface Place {
	project?: string;
	organization?: string;
}

/**
 * The family bar's descriptor (`beyond-family/1`), which this service relays from Beyond Projects. It
 * is navigation with context and grants nothing. The bar never breaks a page: whatever keeps it from
 * being read — Projects not configured or not answering, this service not answering, a refusal — is
 * `{unavailable: true}`, and the bar then shows the names this application knows itself. A read the
 * ended session kept from being sent answers the last descriptor of that place, so the bar stays as it was.
 */
export class Family {
	#client: Client;
	#kept = new Map<string, Descriptor>();

	constructor(client: Client) {
		this.#client = client;
	}

	async describe(place: Place = {}): Promise<Descriptor | { unavailable: true }> {
		const query = new URLSearchParams();
		if (place.project) query.set('project', place.project);
		if (place.organization) query.set('organization', place.organization);
		const search = query.toString();
		try {
			const answer = await this.#client.get<Descriptor | { unavailable: true }>(`/v1/family${search ? `?${search}` : ''}`);
			if (!answer || !('protocol' in answer) || answer.protocol !== 'beyond-family/1') return { unavailable: true };
			this.#kept.set(search, answer);
			return answer;
		} catch (cause) {
			const kept = cause instanceof Refusal && cause.quiet ? this.#kept.get(search) : undefined;
			return kept ?? { unavailable: true };
		}
	}
}
